import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, ChevronDown, ChevronRight, Download, FileText } from 'lucide-react';
import { ProgressionCard, BadgeRing } from './MerkeskytingProgressionCard';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Kompakt kort for Hjem-siden. Vises kun hvis medlemmet har startet merkeskyting. */
const MerkeskytingHomeCard = () => {
  const [progressions, setProgressions] = useState([]);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    Promise.all([
      axios.get(`${API_BASE}/api/merkeskyting/my-progress`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { progressions: [] } })),
      axios.get(`${API_BASE}/api/merkeskyting/my-awards`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { awards: [] } })),
      axios.get(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
    ])
      .then(([p, a, m]) => {
        setProgressions(p.data.progressions || []);
        setAwards(a.data.awards || []);
        setMe(m.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const downloadIndividual = async () => {
    if (!me?.id) return;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/merkeskyting/report/individual/${me.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `min_merkeskyting_status.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const downloadDiploma = async (awardId, level) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/merkeskyting/diploma/${awardId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `diplom_${level}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  if (loading) return null;
  if (progressions.length === 0) return null;

  const primary = progressions[0];
  const active = primary.active_stage;

  return (
    <div className="bg-gray-800 rounded-lg p-4 sm:p-6" data-testid="home-merkeskyting-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-yellow-500 flex-shrink-0" />
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">Merkeskyting</h2>
            <p className="text-gray-400 text-xs sm:text-sm">{primary.discipline_name} – {primary.variant_name}</p>
          </div>
        </div>
        <button onClick={downloadIndividual} className="text-xs px-3 py-1.5 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 inline-flex items-center gap-1" data-testid="home-download-status-btn">
          <FileText className="w-3 h-3" /> Statusrapport
        </button>
      </div>

      {/* Primær progresjon – kompakt */}
      <div className="mt-3 flex items-center gap-3">
        {active ? (
          <>
            <BadgeRing level={active.level} size={44} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-white font-semibold text-sm">Aktivt merke: {active.level_name}</span>
                <span className="text-yellow-400 font-bold text-sm">{active.count}/10</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-500 transition-all" style={{ width: `${active.percent}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">Krav: <strong>{active.threshold}</strong> {primary.unit_label} – {active.percent}% fullført</p>
            </div>
          </>
        ) : (
          <div className="text-green-400 font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5" /> Alle nivåer fullført på {primary.variant_name}!
          </div>
        )}
      </div>

      {/* Oppnådde merker – diplom-nedlasting */}
      {awards.length > 0 && (
        <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-yellow-500/10 to-amber-600/10 border border-yellow-500/30" data-testid="home-my-awards">
          <p className="text-yellow-300 text-xs font-bold uppercase tracking-wider mb-2">Mine oppnådde merker</p>
          <div className="flex flex-wrap gap-2">
            {awards.map(a => (
              <button
                key={a.id}
                onClick={() => downloadDiploma(a.id, a.level)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-100 text-xs font-medium rounded-full border border-yellow-500/40"
                data-testid={`home-diploma-${a.id}`}
                title={`Last ned diplom for ${a.level_name}`}
              >
                <BadgeRing level={a.level} size={16} />
                {a.level_name} – {a.variant_name}
                <Download className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ekspanderbar full merkeoversikt */}
      <button
        onClick={() => setShowAll(!showAll)}
        className="mt-3 inline-flex items-center gap-1 text-xs text-yellow-400 hover:text-yellow-300"
        data-testid="home-merkeskyting-toggle"
      >
        {showAll ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {showAll ? 'Skjul full merkeoversikt' : 'Se full merkeoversikt'}
      </button>

      {showAll && (
        <div className="mt-3 space-y-3" data-testid="home-merkeskyting-full">
          {progressions.map((p, i) => (
            <ProgressionCard key={i} progression={p} defaultExpanded={true} compact />
          ))}
        </div>
      )}
    </div>
  );
};

export default MerkeskytingHomeCard;
